/**
 * Content Calendar, Drafts, Post List/Detail, Overview Attention panel,
 * and Bulk CSV Import — Learn-Mode inline tips.
 */

import type { LearnTip } from "./types";

export const calendarTips: LearnTip[] = [
  {
    key: "social-calendar-filters",
    title: "Calendar Filters",
    body: "Narrow the calendar down to specific post statuses, clients, or platforms so you can focus on what needs attention.",
  },
  {
    key: "social-calendar-format-filter",
    title: "Feed or Stories",
    body: "Switch between everything, feed posts and Reels only, or Stories only. Stories stand on their own here: a Story you posted by itself shows a Story badge, and one that goes out with a feed post shows a small ring.",
  },
  {
    key: "social-calendar-grid",
    title: "Content Calendar",
    body: "Every scheduled and published post laid out by day so you can see your posting cadence at a glance. Switch between week and month view, and use the arrows to look at past or future dates. Drafts wait in the tray beside the calendar until you schedule them.",
  },
  {
    key: "social-calendar-bulk-actions",
    title: "Select Several Posts",
    body: "Turn on Select, click posts (Shift-click selects everything in between), then use the bar at the bottom to move, unschedule, send for approval, or delete them all at once.",
    bullets: [
      "Shift +1 day / +1 week keeps each post's time of day in its own timezone.",
      "It's all or nothing: if one selected post can't take the action, nothing changes and the message says which posts blocked it.",
      "A post's Instagram Story moves with it — select just the post.",
    ],
  },
  {
    key: "social-calendar-notes",
    title: "Calendar Notes & Campaigns",
    body: "Add a note to a day — or give it an end date to draw a campaign band across several days. Notes can be for one client or the whole agency, and for your team or just you.",
    bullets: ["Open a note and choose Convert to post to start a draft with its text already filled in."],
  },
  {
    key: "social-calendar-drafts-tray",
    title: "Unscheduled Drafts",
    body: "Drafts that don't have a publish time yet. Drag one onto a day (or an hour in week view) to schedule it, or use its calendar button.",
  },
  {
    key: "social-calendar-draft-schedule",
    title: "Schedule a Draft",
    body: "Choose the time, timezone and accounts for this draft. Scheduling from the calendar never approves it — if the client requires approval it waits in Approvals first.",
  },
  {
    key: "social-instagram-story",
    title: "Instagram Story",
    body: "Shows whether this Instagram post also goes out as an Instagram Story. A Story is its own post, so this line tells you if one is scheduled with it, already posted, waiting for approval, or failed.",
    bullets: [
      "No Story yet? On an approved, scheduled post with a picture or video, choose Also share to Story — it posts the same picture or video as a Story at the same time, using this post's approval.",
      "Moving the post (drag, Change date & time, or a bulk move) moves its Story with it.",
    ],
  },
  {
    key: "social-instagram-story-confirm",
    title: "Also Share to Story",
    body: "Confirms the Story before it's created. It goes out at the post's time on the same Instagram account. A Reel's Instagram-library music can't be added to a Story, so the Story plays the video's own sound.",
  },
  {
    key: "social-post-change-time",
    title: "Change Date & Time",
    body: "Move one scheduled post to a new day and time without dragging it. Pick the time in the client's timezone; it must be at least 5 minutes from now. If the post has an Instagram Story going out with it, the Story moves too.",
  },
  {
    key: "social-post-list-bulk-actions",
    title: "Bulk Post Actions",
    body: "Act on every selected post at once: send for approval, approve, shift scheduled posts by a number of days, or move unscheduled posts to another client.",
    bullets: [
      "If any selected post can't take the action, nothing changes and the message lists why — the posts that blocked it stay selected.",
      "Approving respects your approval settings: you can't approve posts you submitted yourself when a second reviewer is required.",
    ],
  },
  {
    key: "social-post-duplicate-clients",
    title: "Duplicate to Clients",
    body: "Copy this post into a new draft for each client you pick — handy for agency-wide announcements. Each copy is unscheduled and waits in that client's drafts.",
  },
  {
    key: "social-drafts-toolbar",
    title: "Searching Drafts",
    body: "Search your saved drafts by caption text, and sort them to find the one you're looking for.",
  },
  {
    key: "social-post-list-filters",
    title: "Post Filters",
    body: "Search captions and filter the post list down by network, status, or client to find a specific post quickly.",
  },
  {
    key: "social-post-attach-design",
    title: "Design Not Attached",
    body: "This draft was built in the design editor but its finished images were never attached to the post — Instagram can't publish a post with no image. Click through to render every page of the design and attach it.",
  },
  {
    key: "social-post-previews",
    title: "Network Previews",
    body: "How this post will look on each network it goes to, side by side, as the client's real account: Facebook on the left, Instagram on the right, with the full caption, hashtags, first comment and every photo. Swipe the Instagram photos; Facebook shows its photo grid (tap it to see each photo).",
  },
  {
    key: "social-post-publish-status",
    title: "Publishing Status",
    body: "Shows whether this post published successfully on each selected platform. If a platform failed, you can retry just that one, or retry everything that failed at once.",
  },
  {
    key: "social-post-content-validation",
    title: "Validation",
    body: "The result of the fact check on this post: each check that passed, failed or needs the client's confirmation, any issues found, and the question to ask the client. It is cleared automatically when the caption or media change, so what you see always applies to the current post.",
  },
  {
    key: "social-post-mark-removed",
    title: "Mark Removed on Platform",
    body: "Use this after you have already deleted a published post on Facebook, Instagram or another network yourself. It records the removal so reports and the calendar stop counting the post. It does not delete, hide or unpublish anything on the network.",
  },
  {
    key: "social-post-delete-remote",
    title: "Delete from Facebook or Instagram",
    body: "Permanently deletes a published post from the network, including its likes and comments, then marks it Removed on platform here. It can't be undone. Facebook Page stories can't be deleted by apps, so delete those in Meta Business Suite. Instagram delete needs agency staff to reconnect the account once to grant the permission.",
  },
  {
    key: "social-post-edit-caption",
    title: "Edit a Live Caption",
    body: "Fix the caption of a post that is already live on Facebook without deleting it. Saving changes the live post right away and records who changed it. Instagram doesn't allow caption changes from other apps, so edit Instagram captions in the Instagram app.",
  },
  {
    key: "social-overview-attention",
    title: "Needs Your Attention",
    body: "A rolled-up list of things across all your clients that need a human decision — posts awaiting approval, failed publishes, expiring account connections, and similar. Sorted so the most urgent items show first.",
  },
  {
    key: "social-bulk-import",
    title: "Bulk Schedule from CSV",
    body: "Upload a spreadsheet of posts to schedule many at once instead of creating them one by one. Depending on this client's approval settings, imported posts either publish automatically at their scheduled times or wait in Approvals first.",
  },
  {
    key: "social-bulk-import-wizard",
    title: "Import Posts from CSV",
    body: "Pick your CSV file, check that each column is matched to the right post field, then review the rows before importing. Rows with problems are flagged with the reason; you can skip them and import the rest, then fix and re-upload the skipped ones.",
  },
];
